import { useState } from "react";
import type { SelectChangeEvent, SelectOption } from "./selectOption";

interface SelectValueInput {
  options: SelectOption[];
  /** Controlled value; when absent the select keeps its own. */
  value?: string;
  defaultValue?: string;
  name?: string;
  onChange?: (event: SelectChangeEvent) => void;
  onValueChange?: (value: string) => void;
}

export interface SelectValue {
  value: string;
  selectedIndex: number;
  selected?: SelectOption;
  /** Selects `option` and notifies the listeners, unless it is already the value. */
  change: (option: SelectOption) => void;
}

/** The current value, controlled or not, and how to change it. */
export function useSelectValue({ options, value: controlled, defaultValue, name, onChange, onValueChange }: SelectValueInput): SelectValue {
  const [own, setOwn] = useState(defaultValue ?? options[0]?.value ?? "");
  const value = controlled ?? own;
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  function change(option: SelectOption): void {
    if (option.value === value) return;
    if (controlled === undefined) setOwn(option.value);
    const target = { value: option.value, name: name ?? "" };
    onChange?.({ target, currentTarget: target });
    onValueChange?.(option.value);
  }

  return { value, selectedIndex, selected, change };
}
